import React from 'react'
import { useController } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Text, View } from 'react-native'
import { tv } from 'tailwind-variants'

import type { AnyCallbackConfig } from '@flows/core/types/dispatchedHookTypes'
import type { OtpInput as OtpInputProps } from '@flows/core/types/stepContentTypes'
import type { AnySimpleOnSubmitHook, FlowValues } from '@flows/core/types/stepTypes'
import Button from '@ui-kit/components/Button'
import TextField from '@ui-kit/components/formComponents/TextField'
import { body2, h2, h4 } from '@ui-kit/core/typography'

const otpStyles = tv({
  slots: {
    textInput: 'flex flex-row rounded-lg border border-success',
    otpContainer: 'flex w-full flex-col items-center gap-y-5',
    errorContainer: 'relative flex flex-col rounded-lg bg-error-light p-4',
    buttonContainer: 'w-full',
    resendContainer: 'flex flex-row items-center align-middle',
    verificationContainer: 'flex flex-col items-center gap-y-4 py-8',
    subtitle: [body2(), 'max-w-[500px] text-center'],
  },
})

interface Props<
  TCallbackConfig extends AnyCallbackConfig,
  TSimpleOnSubmitHook extends AnySimpleOnSubmitHook,
> extends OtpInputProps<TCallbackConfig, TSimpleOnSubmitHook> {
  flowValues: FlowValues
}

export const OtpLogin = <TProps extends Props<any, any>>(
  props: TProps & {
    config: {
      submit: () => Promise<void> | void
      isLoading: boolean
      isError: boolean
    }
  }
) => {
  const { otpContainer, resendContainer, verificationContainer, subtitle } = otpStyles()
  const [isResendDisabled, setIsResendDisabled] = React.useState(false)
  const { t } = useTranslation()

  React.useEffect(() => {
    if (isResendDisabled) {
      const timeoutID = setTimeout(() => {
        setIsResendDisabled(false)
      }, 6000)

      return () => clearTimeout(timeoutID)
    }
  }, [isResendDisabled])

  const resend = async () => {
    if (isResendDisabled) {
      return
    }
    setIsResendDisabled(true)
    await props.config.submit()
  }

  const { field, fieldState } = useController({
    name: 'otp',
  })

  const urlSearchParams = new URLSearchParams(window.location.search)
  let email = Object.fromEntries(urlSearchParams.entries()).email

  if (email === undefined) {
    email = t('otp.email')
  }

  return (
    <View className={otpContainer()}>
      <Text className={h2({ type: 'primary', color: 'deepForest' })}>{t('otp.title')}</Text>
      <Text className={subtitle()}>{t('otp.subtitle', { email })}</Text>
      <View className={verificationContainer()}>
        <Text className={h4()}>{t('otp.verificationCode')}</Text>
        <TextField
          testID="otp"
          name="otp"
          value={field.value}
          styleType={'outlined'}
          onValueChange={field.onChange}
          status={fieldState.error?.message ? 'error' : undefined}
          errorMessage={fieldState.error?.message}
        />
        <View className={resendContainer()}>
          <Text className={body2()}>{t('otp.didntGetCode')}</Text>
          <Button
            type="text"
            label={t('otp.resend')}
            onPress={resend}
            disabled={isResendDisabled}
            isLoading={props.config.isLoading}
          />
        </View>
      </View>
    </View>
  )
}
